import { useState, useContext, useCallback, useEffect } from "react";
import { HierarchyContext } from "../context/HierarchyContext";
import { hierarchyService } from "../services/hierarchyService";

/**
 * Hook personalizado para manejar operaciones de jerarquía organizacional
 * Proporciona métodos para navegar y gestionar Plant → Process → Room → Machine → Catalog
 */
export const useHierarchy = () => {
  const context = useContext(HierarchyContext);

  if (!context) {
    throw new Error("useHierarchy debe ser usado dentro de un HierarchyProvider");
  }

  const { state, dispatch } = context;
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Limpia los errores
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Maneja errores de manera consistente
   * @param {Error} err - Error capturado
   * @param {string} defaultMessage - Mensaje por defecto
   */
  const handleError = useCallback((err, defaultMessage) => {
    const errorMessage = err.message || defaultMessage;
    setError(errorMessage);
    console.error(defaultMessage, err);
  }, []);

  /**
   * Carga todas las plantas
   * @returns {Promise<boolean>} True si la carga fue exitosa
   */
  const loadPlants = useCallback(async () => {
    try {
      setIsLoading(true);
      clearError();

      const response = await hierarchyService.getPlants();

      if (response.success) {
        dispatch({
          type: "SET_PLANTS",
          payload: response.data,
        });
        return true;
      } else {
        setError(response.message || "Error al cargar plantas");
        return false;
      }
    } catch (err) {
      handleError(err, "Error de conexión al cargar plantas");
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, clearError, handleError]);

  /**
   * Selecciona una planta y carga sus procesos
   * @param {string} plantId - ID de la planta
   * @returns {Promise<boolean>} True si fue exitoso
   */
  const selectPlant = useCallback(
    async (plantId) => {
      try {
        setIsLoading(true);
        clearError();

        // Encontrar la planta en el estado actual
        const plant = state.plants.find((p) => p._id === plantId);
        if (!plant) {
          setError("Planta no encontrada");
          return false;
        }

        // Cargar procesos de la planta
        const response = await hierarchyService.getProcesses(plantId);

        if (response.success) {
          dispatch({
            type: "SELECT_PLANT",
            payload: {
              plant,
              processes: response.data,
            },
          });
          return true;
        } else {
          setError(response.message || "Error al cargar procesos");
          return false;
        }
      } catch (err) {
        handleError(err, "Error de conexión al seleccionar planta");
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [state.plants, dispatch, clearError, handleError]
  );

  /**
   * Selecciona un proceso y carga sus salas
   * @param {string} processId - ID del proceso
   * @returns {Promise<boolean>} True si fue exitoso
   */
  const selectProcess = useCallback(
    async (processId) => {
      try {
        setIsLoading(true);
        clearError();

        // Encontrar el proceso en el estado actual
        const process = state.processes.find((p) => p._id === processId);
        if (!process) {
          setError("Proceso no encontrado");
          return false;
        }

        // Cargar salas del proceso
        const response = await hierarchyService.getRooms(processId);

        if (response.success) {
          dispatch({
            type: "SELECT_PROCESS",
            payload: {
              process,
              rooms: response.data,
            },
          });
          return true;
        } else {
          setError(response.message || "Error al cargar salas");
          return false;
        }
      } catch (err) {
        handleError(err, "Error de conexión al seleccionar proceso");
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [state.processes, dispatch, clearError, handleError]
  );

  /**
   * Selecciona una sala y carga sus máquinas
   * @param {string} roomId - ID de la sala
   * @returns {Promise<boolean>} True si fue exitoso
   */
  const selectRoom = useCallback(
    async (roomId) => {
      try {
        setIsLoading(true);
        clearError();

        // Encontrar la sala en el estado actual
        const room = state.rooms.find((r) => r._id === roomId);
        if (!room) {
          setError("Sala no encontrada");
          return false;
        }

        // Cargar máquinas de la sala
        const response = await hierarchyService.getMachines(roomId);

        if (response.success) {
          dispatch({
            type: "SELECT_ROOM",
            payload: {
              room,
              machines: response.data,
            },
          });
          return true;
        } else {
          setError(response.message || "Error al cargar máquinas");
          return false;
        }
      } catch (err) {
        handleError(err, "Error de conexión al seleccionar sala");
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [state.rooms, dispatch, clearError, handleError]
  );

  /**
   * Selecciona una máquina y carga sus catálogos
   * @param {string} machineId - ID de la máquina
   * @returns {Promise<boolean>} True si fue exitoso
   */
  const selectMachine = useCallback(
    async (machineId) => {
      try {
        setIsLoading(true);
        clearError();

        // Encontrar la máquina en el estado actual
        const machine = state.machines.find((m) => m._id === machineId);
        if (!machine) {
          setError("Máquina no encontrada");
          return false;
        }

        // Cargar catálogos de la máquina
        const response = await hierarchyService.getCatalogs(machineId);

        if (response.success) {
          dispatch({
            type: "SELECT_MACHINE",
            payload: {
              machine,
              catalogs: response.data,
            },
          });
          return true;
        } else {
          setError(response.message || "Error al cargar catálogos");
          return false;
        }
      } catch (err) {
        handleError(err, "Error de conexión al seleccionar máquina");
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [state.machines, dispatch, clearError, handleError]
  );

  /**
   * Navega hacia atrás en la jerarquía
   * @param {string} level - Nivel al que navegar ('plant', 'process', 'room', 'machine')
   */
  const navigateBack = useCallback(
    (level) => {
      dispatch({
        type: "NAVIGATE_BACK",
        payload: level,
      });
    },
    [dispatch]
  );

  /**
   * Resetea toda la navegación a la vista inicial
   */
  const resetNavigation = useCallback(() => {
    dispatch({ type: "RESET_NAVIGATION" });
  }, [dispatch]);

  /**
   * Recarga el nivel actual de la jerarquía
   * @returns {Promise<boolean>} True si fue exitoso
   */
  const reloadCurrentLevel = useCallback(async () => {
    const { selectedPlant, selectedProcess, selectedRoom, selectedMachine } = state;

    if (selectedMachine) {
      return await selectMachine(selectedMachine._id);
    } else if (selectedRoom) {
      return await selectRoom(selectedRoom._id);
    } else if (selectedProcess) {
      return await selectProcess(selectedProcess._id);
    } else if (selectedPlant) {
      return await selectPlant(selectedPlant._id);
    } else {
      return await loadPlants();
    }
  }, [state, selectMachine, selectRoom, selectProcess, selectPlant, loadPlants]);

  /**
   * Construye el breadcrumb path actual
   * @returns {Array} Array de objetos con name, id y level
   */
  const getBreadcrumbPath = useCallback(() => {
    const path = [];
    const { selectedPlant, selectedProcess, selectedRoom, selectedMachine } = state;

    if (selectedPlant) {
      path.push({
        name: selectedPlant.name,
        id: selectedPlant._id,
        level: "plant",
      });
    }

    if (selectedProcess) {
      path.push({
        name: selectedProcess.name,
        id: selectedProcess._id,
        level: "process",
      });
    }

    if (selectedRoom) {
      path.push({
        name: selectedRoom.name,
        id: selectedRoom._id,
        level: "room",
      });
    }

    if (selectedMachine) {
      path.push({
        name: selectedMachine.name,
        id: selectedMachine._id,
        level: "machine",
      });
    }

    return path;
  }, [state]);

  /**
   * Obtiene el nivel actual de navegación
   * @returns {string} Nivel actual ('plants', 'processes', 'rooms', 'machines', 'catalogs')
   */
  const getCurrentLevel = useCallback(() => {
    const { selectedPlant, selectedProcess, selectedRoom, selectedMachine } = state;

    if (selectedMachine) return "catalogs";
    if (selectedRoom) return "machines";
    if (selectedProcess) return "rooms";
    if (selectedPlant) return "processes";
    return "plants";
  }, [state]);

  /**
   * Verifica si se puede navegar hacia atrás
   * @returns {boolean} True si se puede navegar hacia atrás
   */
  const canNavigateBack = useCallback(() => {
    return !!(state.selectedPlant || state.selectedProcess || state.selectedRoom || state.selectedMachine);
  }, [state]);

  /**
   * Obtiene el elemento padre del nivel actual
   * @returns {Object|null} Elemento padre o null si está en el nivel raíz
   */
  const getParentElement = useCallback(() => {
    const { selectedPlant, selectedProcess, selectedRoom, selectedMachine } = state;

    if (selectedMachine) return selectedRoom;
    if (selectedRoom) return selectedProcess;
    if (selectedProcess) return selectedPlant;
    return null;
  }, [state]);

  /**
   * Busca en todos los niveles de la jerarquía
   * @param {string} searchTerm - Término de búsqueda
   * @param {Array} levels - Niveles a buscar ['plants', 'processes', 'rooms', 'machines', 'catalogs']
   * @returns {Promise<Object>} Resultados de búsqueda organizados por nivel
   */
  const searchHierarchy = useCallback(
    async (searchTerm, levels = ["plants", "processes", "rooms", "machines", "catalogs"]) => {
      try {
        setIsLoading(true);
        clearError();

        const response = await hierarchyService.searchHierarchy(searchTerm, levels);

        if (response.success) {
          return response.data;
        } else {
          setError(response.message || "Error en la búsqueda");
          return {};
        }
      } catch (err) {
        handleError(err, "Error de conexión en la búsqueda");
        return {};
      } finally {
        setIsLoading(false);
      }
    },
    [clearError, handleError]
  );

  // Efecto para cargar plantas inicialmente si no están cargadas
  // NOTA: Comentado para evitar cargar datos antes de la autenticación
  // Las páginas individuales deben llamar a loadPlants() cuando sea necesario
  /*
  useEffect(() => {
    if (state.plants.length === 0 && !isLoading) {
      loadPlants();
    }
  }, [state.plants.length, isLoading, loadPlants]);
  */

  return {
    // Estado
    plants: state.plants,
    processes: state.processes,
    rooms: state.rooms,
    machines: state.machines,
    catalogs: state.catalogs,
    selectedPlant: state.selectedPlant,
    selectedProcess: state.selectedProcess,
    selectedRoom: state.selectedRoom,
    selectedMachine: state.selectedMachine,
    isLoading,
    error,

    // Métodos de navegación
    loadPlants,
    selectPlant,
    selectProcess,
    selectRoom,
    selectMachine,
    navigateBack,
    resetNavigation,
    reloadCurrentLevel,

    // Utilidades
    getBreadcrumbPath,
    getCurrentLevel,
    canNavigateBack,
    getParentElement,
    searchHierarchy,
    clearError,
  };
};
